import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    CircularProgress,
    Alert,
    IconButton,
    TextField,
    Checkbox,
    Tooltip
} from '@mui/material';
import {
    DataGrid,
    GridToolbarContainer,
    GridActionsCellItem,
    GridRowModes,
    GridRowEditStopReasons,
} from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';

import {
    useListEventCategories,
    useCreateEventCategory,
    useUpdateEventCategory,
    useDeleteEventCategory
} from '../services/appService';
import ConfirmDialog from './ConfirmDialog';

function EditToolbar(props) {
    const { setRows, setRowModesModel, noun } = props;

    const handleClick = () => {
        const id = Date.now(); // Temporary ID for new row
        setRows((oldRows) => [
            ...oldRows,
            {
                id, name: '', display_order: 0, is_admin_only: false, is_highlighted: false, isNew: true
            },
        ]);
        setRowModesModel((oldModel) => ({
            ...oldModel,
            [id]: { mode: GridRowModes.Edit, fieldToFocus: 'name' },
        }));
    };

    return (
        <GridToolbarContainer>
            <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
                Add {noun}
            </Button>
        </GridToolbarContainer>
    );
}

EditToolbar.propTypes = {
    setRowModesModel: PropTypes.func.isRequired,
    setRows: PropTypes.func.isRequired,
    noun: PropTypes.string.isRequired,
};

function EventCategoriesConfigModal({ open, onClose, appId }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [rowModesModel, setRowModesModel] = useState({});
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const listCategoriesHook = useListEventCategories();
    const createCategoryHook = useCreateEventCategory();
    const updateCategoryHook = useUpdateEventCategory();
    const deleteCategoryHook = useDeleteEventCategory();

    const fetchCategories = useCallback(async () => {
        if (open && appId) {
            setLoading(true);
            setError('');
            try {
                const data = await listCategoriesHook(appId);
                setRows(data || []);
            } catch (err) {
                console.error("Error fetching event categories:", err);
                setError(err.message || 'Failed to load categories.');
            } finally {
                setLoading(false);
            }
        }
    }, [open, appId, listCategoriesHook]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const handleRowEditStop = (params, event) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = true;
        }
    };

    const handleEditClick = (id) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    };

    const handleSaveClick = (id) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    };

    const handleDeleteClick = (id) => () => {
        setItemToDelete(id);
        setConfirmDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!itemToDelete) return;
        setLoading(true);
        setError('');
        try {
            await deleteCategoryHook(appId, itemToDelete);
            setRows(rows.filter((row) => row.id !== itemToDelete));
             fetchCategories(); // Re-fetch to be sure
        } catch (err) {
            console.error("Error deleting category:", err);
            setError(err.message || 'Failed to delete category.');
            setLoading(false);
        } finally {
            setConfirmDialogOpen(false);
            setItemToDelete(null);
            // Loading set by fetchCategories
        }
    };

    const handleCancelClick = (id) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        });

        const editedRow = rows.find((row) => row.id === id);
        if (editedRow?.isNew) {
            setRows(rows.filter((row) => row.id !== id));
        }
    };

    const processRowUpdate = async (newRow) => {
        setLoading(true);
        setError('');
        const updatedRow = { ...newRow, isNew: false };
        try {
            let savedRow;
            if (newRow.isNew) {
                // Create: remove temporary ID before sending
                const { id, ...dataToCreate } = updatedRow;
                savedRow = await createCategoryHook(appId, dataToCreate);
                 // Replace temporary row with saved row (including real ID)
                setRows(rows.map((row) => (row.id === newRow.id ? { ...savedRow, id: savedRow.id } : row)));
            } else {
                // Update
                savedRow = await updateCategoryHook(appId, newRow.id, updatedRow);
                setRows(rows.map((row) => (row.id === newRow.id ? savedRow : row)));
            }
             fetchCategories(); // Re-fetch to ensure sync
            return savedRow;
        } catch (err) {
            console.error("Error saving category:", err);
            setError(err.message || 'Failed to save category.');
            setLoading(false);
            const oldRow = rows.find(r => r.id === newRow.id);
            return oldRow ?? newRow;
        } finally {
           // setLoading handled by fetch or error
        }
    };

    const handleRowModesModelChange = (newRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };

    const handleProcessRowUpdateError = useCallback((error) => {
        console.error("Error processing row update:", error);
        setError(error.message || 'An error occurred while saving.');
    }, []);


    const columns = [
        { field: 'name', headerName: 'Category Name', width: 200, editable: true },
        {
            field: 'display_order',
            headerName: 'Order',
            type: 'number',
            width: 90,
            align: 'left',
            headerAlign: 'left',
            editable: true,
        },
        {
            field: 'is_admin_only',
            headerName: 'Admin Only?',
            type: 'boolean',
            width: 120,
            editable: true,
        },
        {
            field: 'is_highlighted',
            headerName: 'Highlight?',
            type: 'boolean',
            width: 120,
            editable: true,
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

                if (isInEditMode) {
                    return [
                        <GridActionsCellItem
                            icon={<SaveIcon />}
                            label="Save"
                            sx={{ color: 'primary.main' }}
                            onClick={handleSaveClick(id)}
                        />,
                        <GridActionsCellItem
                            icon={<CancelIcon />}
                            label="Cancel"
                            className="textPrimary"
                            onClick={handleCancelClick(id)}
                            color="inherit"
                        />,
                    ];
                }

                return [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label="Edit"
                        className="textPrimary"
                        onClick={handleEditClick(id)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label="Delete"
                        onClick={handleDeleteClick(id)}
                        color="inherit"
                    />,
                ];
            },
        },
    ];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SettingsIcon />
                    Manage Event Categories
                 </Box>
            </DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Box sx={{ height: 500, width: '100%' }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        editMode="row"
                        rowModesModel={rowModesModel}
                        onRowModesModelChange={handleRowModesModelChange}
                        onRowEditStop={handleRowEditStop}
                        processRowUpdate={processRowUpdate}
                        onProcessRowUpdateError={handleProcessRowUpdateError}
                        slots={{ toolbar: EditToolbar }}
                        slotProps={{
                            toolbar: { setRows, setRowModesModel, noun: 'Category' },
                        }}
                        loading={loading}
                        initialState={{
                            sorting: {
                                sortModel: [{ field: 'display_order', sort: 'asc' }],
                            },
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>

            <ConfirmDialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Category?"
                message={`Are you sure you want to delete this event category? This might affect existing events using it.`}
            />
        </Dialog>
    );
}

EventCategoriesConfigModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    appId: PropTypes.string.isRequired,
};

export default EventCategoriesConfigModal; 